import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { useConversationUuid } from '~/hooks'
import { SocketEvent } from '~/services/socket'
import { getConversationWithTasks } from '~/services/conversationService'
import { ConversationMetadata, ConversationWithTasks } from '~/types/Conversations'
import type { Message } from '~/components/chat/message'
import type { TaskObjectWithNoDetails } from '~/types/TaskType'
import { AppEvents, globalEventEmitter } from '~/utils/shared'
import { useConversationsContext } from './ConversationContext'
import { useWorkspaceContext } from './WorkspaceContext'

/**
 * CurrentConversationContext
 *
 * Architecture: This context manages its own state independently from the sidebar's
 * conversation list. When the URL changes to a conversation route, this context
 * fetches that conversation directly from the API.
 *
 * Why independent?
 * - The sidebar list is paginated and may not contain the current conversation
 * - Avoids race conditions between list fetches and single conversation fetches
 * - Cleaner separation of concerns: list browsing vs current conversation viewing
 *
 * Sidebar Sync:
 * - When this context fetches/refreshes a conversation, it also syncs the data
 *   to the sidebar list via upsertConversation (update if exists, add if not)
 * - This ensures task status changes are reflected in both views
 *
 * Credit Limit Enforcement:
 * - For users over credit limit, the API returns data_hidden: true
 * - When this happens, conversation content is hidden but we track the credit info
 *   so the UI can show an appropriate upgrade message
 */

// Context state interface
interface CurrentConversationState {
  conversation: ConversationWithTasks | null
  isLoading: boolean
  showLoadingIndicator: boolean
  error: string | null
  uuid: string | null
  refreshCurrentConversation: (silent?: boolean) => Promise<void>
  // Convenience accessors (derived from conversation)
  inline_messages: Message[]
  tasks: TaskObjectWithNoDetails[]
  refetch: (silent?: boolean) => Promise<void>
  // Message pagination
  hasMoreMessages: boolean
  isLoadingMore: boolean
  loadMoreMessages: () => Promise<void>
  // Credit limit enforcement
  dataHidden: boolean
  dataHiddenReason: 'credit_limit_exceeded' | null
  isFreeLicense: boolean
  creditsUsed: number
  creditsLimit: number
}

// Create the context
const CurrentConversationContext = createContext<CurrentConversationState | null>(null)

const TASK_MESSAGE_REFRESH_STATUSES = new Set([
  'DIFF_GENERATED',
  'DRAFT_GENERATED',
  'DOCUMENT_GENERATED',
  'MUNIT_DIFF_GENERATED',
  'INTEGRATION_TEST_DIFF_GENERATED',
  'MIGRATION_REFACTOR_ACCELERATOR_DIFF_GENERATED',
  'MIGRATION_CODE_GENERATED',
  'REFACTOR_CODE_GENERATED',
  'COMPLETED',
  'ERRORED',
  'STOPPED'
])
// Task status can arrive before the assistant summary/canvas blocks are committed.
// Retry silently so the transcript catches up without forcing a page refresh.
const TASK_MESSAGE_REFRESH_RETRY_DELAYS_MS = [900, 1300, 2500] as const

// Provider component
interface CurrentConversationProviderProps {
  children: React.ReactNode
}

export const CurrentConversationProvider: React.FC<CurrentConversationProviderProps> = ({
  children
}) => {
  const uuid = useConversationUuid()
  const { currentWorkspace } = useWorkspaceContext()
  const workspaceUuid = currentWorkspace?.uuid

  // Get upsertConversation to sync data to sidebar
  const { upsertConversation } = useConversationsContext()

  // Independent state for the current conversation
  const [conversation, setConversation] = useState<ConversationWithTasks | null>(null)
  const [isFetching, setIsFetching] = useState(false)
  const [showLoading, setShowLoading] = useState(false)
  const [hasLoaded, setHasLoaded] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Message pagination state
  const [hasMoreMessages, setHasMoreMessages] = useState(false)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const oldestMessageIdRef = useRef<number | undefined>(undefined)
  // INVARIANT (FRAGILE): `conversationStateRef` MUST mirror `conversation`
  // and MUST be cleared when the active conversation/workspace changes (see
  // the workspaceChanged/uuidChanged effect below). Callbacks like
  // `fetchConversation` use this ref instead of the `conversation` state to
  // read the latest in-memory list when merging silent refetches. Reading
  // state directly would either capture a stale closure (callbacks aren't
  // recreated on every conversation change) or force the callback to be
  // re-created so often that downstream effects thrash. Either way the
  // pagination merge breaks. Always update both this ref AND the state in
  // the same flow (see `setConversation(nextConversation)` immediately
  // after `conversationStateRef.current = nextConversation`).
  const conversationStateRef = useRef<ConversationWithTasks | null>(null)

  // Credit limit enforcement state
  const [dataHidden, setDataHidden] = useState(false)
  const [dataHiddenReason, setDataHiddenReason] = useState<'credit_limit_exceeded' | null>(null)
  const [isFreeLicense, setIsFreeLicense] = useState(false)
  const [creditsUsed, setCreditsUsed] = useState(0)
  const [creditsLimit, setCreditsLimit] = useState(0)

  // Abort controller for cancelling in-flight requests
  const abortControllerRef = useRef<AbortController | null>(null)
  const loadingTimerRef = useRef<NodeJS.Timeout | null>(null)
  const prevUuidRef = useRef<string | null>(null)
  const prevWorkspaceUuidRef = useRef<string | undefined>(workspaceUuid)

  // Clear state when conversation or workspace changes.
  // Workspace change is critical: if we don't clear here, the fetch effect
  // can fire with a stale conversation UUID against the new workspace → 404.
  useEffect(() => {
    const workspaceChanged = workspaceUuid !== prevWorkspaceUuidRef.current
    const uuidChanged = uuid !== prevUuidRef.current

    if (workspaceChanged || uuidChanged) {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
        abortControllerRef.current = null
      }
      setConversation(null)
      conversationStateRef.current = null
      setHasLoaded(false)
      setShowLoading(false)
      setIsFetching(false)
      setError(null)
      setDataHidden(false)
      setDataHiddenReason(null)
      setHasMoreMessages(false)
      oldestMessageIdRef.current = undefined
      if (loadingTimerRef.current) {
        clearTimeout(loadingTimerRef.current)
        loadingTimerRef.current = null
      }
      prevUuidRef.current = uuid
      prevWorkspaceUuidRef.current = workspaceUuid
    }
  }, [uuid, workspaceUuid])

  // Delayed loading: only show loading UI if fetch takes > 500ms
  useEffect(() => {
    if (!isFetching || conversation) {
      setShowLoading(false)
      if (loadingTimerRef.current) {
        clearTimeout(loadingTimerRef.current)
        loadingTimerRef.current = null
      }
      return
    }

    loadingTimerRef.current = setTimeout(() => {
      setShowLoading(true)
    }, 500)

    return () => {
      if (loadingTimerRef.current) {
        clearTimeout(loadingTimerRef.current)
        loadingTimerRef.current = null
      }
    }
  }, [isFetching, conversation])

  // Fetch conversation directly from API
  // silent: if true, don't set loading state (for background refreshes)
  const fetchConversation = useCallback(
    async (conversationUuid: string, silent = false) => {
      if (!workspaceUuid) return

      // Cancel any in-flight request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
      abortControllerRef.current = new AbortController()

      // Only set fetching state if not silent
      if (!silent) {
        setIsFetching(true)
      }
      setError(null)

      try {
        const data = await getConversationWithTasks(workspaceUuid, conversationUuid)

        // Only update if this request wasn't aborted
        if (abortControllerRef.current?.signal.aborted) return

        // Check if data is hidden due to credit limit
        if (data.data_hidden) {
          setDataHidden(true)
          setDataHiddenReason(data.data_hidden_reason ?? null)
          setIsFreeLicense(data.is_free_license ?? false)
          setCreditsUsed(data.credits_used ?? 0)
          setCreditsLimit(data.credits_limit ?? 0)
          setConversation(null)
          setHasLoaded(true)
          if (!silent) {
            setIsFetching(false)
          }
          return
        }

        // Normal case - data is visible
        setDataHidden(false)
        setDataHiddenReason(null)

        // ─────────────────────────────────────────────────────────────────
        // INVARIANT (FRAGILE): `fetchConversation` MUST merge incoming
        // `inline_messages` with the existing in-memory list when paginated
        // history exists. DO NOT "simplify" by replacing the whole array
        // with `data.inline_messages` even though that looks cleaner.
        // ─────────────────────────────────────────────────────────────────
        // The /detail endpoint always returns the LATEST N messages with
        // no cursor — it has no awareness of what the client loaded via
        // `loadMoreMessages` (which uses `beforeMessageId`). Silent
        // refetches fire frequently (post-stream cleanup, processing-end
        // snapshot, polling, etc.). If we replace the array, every silent
        // refetch wipes whatever older history the user paginated into,
        // and `hasMoreMessages` snaps back to the latest-page value —
        // so it looks like pagination is "broken" or "lost".
        //
        // The merge below uses an id-keyed Map with INCOMING winning on
        // conflict, so server-side mutations (status updates, content
        // edits) still propagate. `havePaginatedOlder` preserves the
        // older `oldest_message_id` / `has_more_messages` so the next
        // `loadMoreMessages` continues from the correct cursor.
        // Guarded by the pagination merge tests in
        // CurrentConversationContext.test.tsx ("preserves paginated older
        // messages across silent refetches").
        const prev = conversationStateRef.current
        const incomingMessages = data.inline_messages ?? []
        const incomingOldest = data.oldest_message_id
        const incomingHasMore = data.has_more_messages ?? false

        let nextConversation: ConversationWithTasks = data
        let nextHasMore = incomingHasMore
        let nextOldestId = incomingOldest

        if (prev) {
          const existingMessages = prev.inline_messages ?? []
          const existingOldest = oldestMessageIdRef.current

          // Detect paginated history: we previously loaded messages older
          // than what the latest fetch returns. Preserve those by merging.
          const havePaginatedOlder =
            existingOldest != null && incomingOldest != null && existingOldest < incomingOldest

          if (havePaginatedOlder || existingMessages.length > incomingMessages.length) {
            const byId = new Map<number, Message>()
            for (const msg of existingMessages) {
              if (msg && typeof msg.id === 'number') byId.set(msg.id, msg)
            }
            // Incoming wins on conflict so server-side mutations
            // (status, content updates) override stale local copies.
            for (const msg of incomingMessages) {
              if (msg && typeof msg.id === 'number') byId.set(msg.id, msg)
            }
            const merged = Array.from(byId.values()).sort((a, b) => a.id - b.id)

            nextConversation = {
              ...data,
              inline_messages: merged,
              has_more_messages: havePaginatedOlder ? prev.has_more_messages : incomingHasMore,
              oldest_message_id: havePaginatedOlder ? existingOldest : incomingOldest
            }
            nextHasMore = havePaginatedOlder ? (prev.has_more_messages ?? false) : incomingHasMore
            nextOldestId = havePaginatedOlder ? existingOldest : incomingOldest
          }
        }

        conversationStateRef.current = nextConversation
        setConversation(nextConversation)
        setHasLoaded(true)
        setHasMoreMessages(nextHasMore)
        oldestMessageIdRef.current = nextOldestId
        if (!silent) {
          setIsFetching(false)
        }

        // Sync to sidebar list (update if exists, add if not)
        // Only sync if we have a valid conversation with uuid
        if (data.uuid) {
          upsertConversation(data)
        }
      } catch (err) {
        // Ignore abort errors
        if (err instanceof Error && err.name === 'AbortError') {
          return
        }
        if (!abortControllerRef.current?.signal.aborted) {
          console.error(
            `[CurrentConversation] Failed to fetch conversation ${conversationUuid}:`,
            err
          )
          setError(err instanceof Error ? err.message : 'Failed to fetch conversation')
          if (!silent) {
            setIsFetching(false)
          }
        }
      }
    },
    [workspaceUuid, upsertConversation]
  )

  // Refresh function exposed to consumers
  // silent: if true, don't trigger loading state changes (for background refreshes after streaming)
  const refreshCurrentConversation = useCallback(
    async (silent = false) => {
      if (uuid) {
        await fetchConversation(uuid, silent)
      }
    },
    [uuid, fetchConversation]
  )

  // Load older messages (cursor-based pagination)
  const loadMoreMessages = useCallback(async () => {
    if (!uuid || !workspaceUuid || !hasMoreMessages || isLoadingMore) return
    const beforeId = oldestMessageIdRef.current
    if (beforeId == null) return

    setIsLoadingMore(true)
    try {
      const olderPage = await getConversationWithTasks(workspaceUuid, uuid, {
        beforeMessageId: beforeId
      })
      if (olderPage.data_hidden) return

      const olderMessages = olderPage.inline_messages ?? []
      setHasMoreMessages(olderPage.has_more_messages ?? false)
      oldestMessageIdRef.current = olderPage.oldest_message_id

      setConversation(prev => {
        if (!prev) return prev
        const existingMessages = prev.inline_messages ?? []
        // ─────────────────────────────────────────────────────────────────
        // INVARIANT (FRAGILE): Older-message append MUST dedupe by id, NOT
        // simply prepend `olderMessages` to `existingMessages`. A silent
        // refetch can reset `oldestMessageIdRef.current` to a more recent
        // cursor than what we've already loaded; the next `loadMoreMessages`
        // then fetches a page that overlaps the already-loaded range. A
        // naive prepend produces visible duplicate messages in the chat
        // history. Existing wins on conflict here (newer in-memory copy
        // takes precedence over the paginated re-fetch). Guarded by
        // `loadMoreMessages dedupe` tests in
        // CurrentConversationContext.test.tsx.
        const byId = new Map<number, Message>()
        for (const msg of olderMessages) {
          if (msg && typeof msg.id === 'number') byId.set(msg.id, msg)
        }
        for (const msg of existingMessages) {
          if (msg && typeof msg.id === 'number') byId.set(msg.id, msg)
        }
        const mergedMessages = Array.from(byId.values()).sort((a, b) => a.id - b.id)

        const next: ConversationWithTasks = {
          ...prev,
          inline_messages: mergedMessages,
          has_more_messages: olderPage.has_more_messages,
          oldest_message_id: olderPage.oldest_message_id
        }
        conversationStateRef.current = next
        return next
      })
    } catch (err) {
      console.error('[CurrentConversation] Failed to load more messages:', err)
    } finally {
      setIsLoadingMore(false)
    }
  }, [uuid, workspaceUuid, hasMoreMessages, isLoadingMore])

  // Fetch conversation when UUID changes
  useEffect(() => {
    if (!uuid) {
      setConversation(null)
      setDataHidden(false)
      setDataHiddenReason(null)
      setIsFetching(false)
      setShowLoading(false)
      setHasLoaded(false)
      setError(null)
      return
    }

    if (!workspaceUuid) {
      return
    }

    fetchConversation(uuid)

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [uuid, workspaceUuid, fetchConversation])

  // Stable ref for the current conversation ID so WS handler always has the latest
  const conversationIdRef = useRef<number | undefined>(conversation?.id)
  conversationIdRef.current = conversation?.id
  // Mirror the latest conversation state so silent refetches can merge against
  // it without re-creating fetchConversation on every render.
  conversationStateRef.current = conversation

  // Batched WS processing — buffer rapid-fire events and apply as a single
  // state update every 500ms to avoid overwhelming React's render pipeline.
  const pendingConvUpdateRef = useRef<Record<string, unknown> | null>(null)
  const pendingTaskUpdatesRef = useRef<Map<number, Record<string, unknown>>>(new Map())
  const wsFlushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const taskMessageRefreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const taskMessageRefreshSequenceRef = useRef(0)

  const flushWsUpdates = useCallback(() => {
    wsFlushTimerRef.current = null

    const pendingConv = pendingConvUpdateRef.current
    const pendingTasks = new Map(pendingTaskUpdatesRef.current)
    pendingConvUpdateRef.current = null
    pendingTaskUpdatesRef.current.clear()

    if (!pendingConv && pendingTasks.size === 0) return

    setConversation(prev => {
      if (!prev) return prev
      let merged = prev

      if (pendingConv) {
        merged = { ...merged }
        if (pendingConv.title !== undefined) merged.title = pendingConv.title as string
        if (pendingConv.updated_time !== undefined)
          merged.updated_time = pendingConv.updated_time as string
        const wsMetadata = pendingConv.conversation_metadata as Record<string, unknown> | undefined
        if (wsMetadata) {
          if ('is_processing' in wsMetadata) {
            merged.is_processing = Boolean(wsMetadata.is_processing)
          }
          merged.conversation_metadata = {
            ...prev.conversation_metadata,
            ...(wsMetadata as Partial<ConversationMetadata>)
          } as ConversationMetadata
        }
      }

      if (pendingTasks.size > 0 && merged.tasks) {
        let tasksChanged = false
        const newTasks = merged.tasks.map(t => {
          const update = pendingTasks.get(t.task_id)
          if (!update) return t
          tasksChanged = true
          const updatedTask = { ...t }
          if (update.status !== undefined) updatedTask.status = update.status as string
          if (update.cmd_type) updatedTask.cmd_type = update.cmd_type as string
          if (update.updated_ts !== undefined) updatedTask.updated_ts = update.updated_ts as string
          return updatedTask
        })
        if (tasksChanged) {
          merged = { ...merged, tasks: newTasks }
        }
      }

      return merged === prev ? prev : merged
    })
  }, [])

  const scheduleWsFlush = useCallback(() => {
    if (wsFlushTimerRef.current) return
    wsFlushTimerRef.current = setTimeout(flushWsUpdates, 500)
  }, [flushWsUpdates])

  const clearTaskMessageRefreshTimer = useCallback(() => {
    taskMessageRefreshSequenceRef.current += 1
    if (taskMessageRefreshTimerRef.current) {
      clearTimeout(taskMessageRefreshTimerRef.current)
      taskMessageRefreshTimerRef.current = null
    }
  }, [])

  const scheduleTaskMessageRefresh = useCallback(() => {
    clearTaskMessageRefreshTimer()
    const sequence = taskMessageRefreshSequenceRef.current

    const runAttempt = (attemptIndex: number) => {
      taskMessageRefreshTimerRef.current = setTimeout(() => {
        taskMessageRefreshTimerRef.current = null
        void refreshCurrentConversation(true).finally(() => {
          if (taskMessageRefreshSequenceRef.current !== sequence) return
          const nextAttemptIndex = attemptIndex + 1
          if (nextAttemptIndex < TASK_MESSAGE_REFRESH_RETRY_DELAYS_MS.length) {
            runAttempt(nextAttemptIndex)
          }
        })
      }, TASK_MESSAGE_REFRESH_RETRY_DELAYS_MS[attemptIndex])
    }

    runAttempt(0)
  }, [clearTaskMessageRefreshTimer, refreshCurrentConversation])

  useEffect(() => {
    return () => {
      if (wsFlushTimerRef.current) clearTimeout(wsFlushTimerRef.current)
      clearTaskMessageRefreshTimer()
    }
  }, [clearTaskMessageRefreshTimer])

  // Listen for WebSocket events and buffer optimistic state merges.
  useEffect(() => {
    if (!uuid) return

    const handler = (...args: unknown[]) => {
      const data = args[0] as {
        event: string
        data?: Record<string, unknown>
      }

      if (data.event === SocketEvent.ConversationUpdated) {
        if ((data.data?.uuid as string) !== uuid) return
        // The backend sends full conversation state on each WS event,
        // so overwriting (not merging) is safe — the latest event is authoritative.
        pendingConvUpdateRef.current = data.data!
        scheduleWsFlush()
      } else if (data.event === SocketEvent.TaskUpdated) {
        const eventConvId = Number(data.data?.conversation_id)
        if (!eventConvId || eventConvId !== conversationIdRef.current) return

        const taskId = Number(data.data!.task_id)
        if (!Number.isFinite(taskId)) return
        pendingTaskUpdatesRef.current.set(taskId, data.data!)
        scheduleWsFlush()

        const status = String(data.data?.status ?? '').toUpperCase()
        if (TASK_MESSAGE_REFRESH_STATUSES.has(status)) {
          scheduleTaskMessageRefresh()
        }
      }
    }

    globalEventEmitter.on(AppEvents.SocketEvent, handler)
    return () => {
      globalEventEmitter.off(AppEvents.SocketEvent, handler)
      clearTaskMessageRefreshTimer()
    }
  }, [uuid, scheduleWsFlush, scheduleTaskMessageRefresh, clearTaskMessageRefreshTimer])

  const refetch = useCallback(
    async (silent?: boolean) => {
      await refreshCurrentConversation(silent)
    },
    [refreshCurrentConversation]
  )

  const inlineMessages = useMemo(
    () => conversation?.inline_messages || [],
    [conversation?.inline_messages]
  )
  const tasks = useMemo(() => conversation?.tasks || [], [conversation?.tasks])

  const contextValue: CurrentConversationState = {
    conversation,
    isLoading: !hasLoaded,
    showLoadingIndicator: showLoading,
    error,
    uuid,
    refreshCurrentConversation,
    inline_messages: inlineMessages,
    tasks,
    refetch,
    hasMoreMessages,
    isLoadingMore,
    loadMoreMessages,
    dataHidden,
    dataHiddenReason,
    isFreeLicense,
    creditsUsed,
    creditsLimit
  }

  return (
    <CurrentConversationContext.Provider value={contextValue}>
      {children}
    </CurrentConversationContext.Provider>
  )
}

// Hook to use the current conversation context
export const useCurrentConversation = () => {
  const context = useContext(CurrentConversationContext)
  if (!context) {
    throw new Error('useCurrentConversation must be used within a CurrentConversationProvider')
  }
  return context
}
